import {
    AuthError,
    KNOWN_AUTH_CODES,
    parseRole,
    type AuthErrorCode,
    type AuthenticatedSession,
    type AuthenticatedUser,
} from '@/domain/auth'
import type { AuthApi } from '@/ports/auth-api'

/**
 * HttpAuthApi : adapter `fetch` qui parle aux routes `/auth/*` du
 * control-plane. Le backend utilise des cookies HttpOnly pour la
 * session, donc on fixe `credentials: 'include'` partout (sans ça les
 * cookies ne sont pas envoyés en cross-origin — utile aussi en dev
 * derrière le proxy Vite).
 *
 * Toutes les erreurs sont normalisées en `AuthError` :
 *   - 4xx/5xx body JSON `{error:{code,message,type}}` → AuthError typée
 *   - 4xx/5xx sans JSON parsable → AuthError('unknown', status)
 *   - throw fetch (DNS/CORS/abort) → AuthError('network_error')
 *
 * Anti-fuite de credentials : l'adapter ne stocke RIEN (ni session
 * token, ni csrf, ni email). C'est le rôle de l'application layer
 * (session store) — l'adapter reste sans état.
 */
export class HttpAuthApi implements AuthApi {
    private readonly baseURL: string

    /**
     * baseURL préfixe toutes les URLs auth. Défaut "" = chemins relatifs
     * (le navigateur les résout contre l'origin courant, idéal en dev
     * derrière le proxy Vite et en prod servi par le control-plane).
     */
    constructor(baseURL = '') {
        this.baseURL = baseURL.replace(/\/+$/, '')
    }

    async loginLocal(email: string, password: string): Promise<AuthenticatedUser> {
        const resp = await this.send('/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        })
        if (!resp.ok) {
            throw await readAuthError(resp)
        }
        const body = (await resp.json()) as { user?: unknown }
        return parseUser(body.user)
    }

    async logout(csrfToken: string | null): Promise<void> {
        const headers: Record<string, string> = {}
        if (csrfToken) {
            // Le backend route /auth/logout n'est PAS derrière le CSRF
            // middleware (cf. cmd/control-plane/main.go : la chaîne CSRF est
            // sur /api/v1/*). On envoie quand même le header par symétrie —
            // ça reste inoffensif si le middleware l'ignore.
            headers['X-CSRF-Token'] = csrfToken
        }
        const resp = await this.send('/auth/logout', { method: 'POST', headers })
        if (!resp.ok && resp.status !== 204) {
            throw await readAuthError(resp)
        }
    }

    async me(): Promise<AuthenticatedSession> {
        const resp = await this.send('/auth/me', { method: 'GET' })
        if (!resp.ok) {
            throw await readAuthError(resp)
        }
        const body = (await resp.json()) as { user?: unknown; csrf_token?: unknown }
        const user = parseUser(body.user)
        const csrfToken = typeof body.csrf_token === 'string' ? body.csrf_token : ''
        if (!csrfToken) {
            // Pas de CSRF token = backend cassé, on refuse de poursuivre :
            // sinon toutes les requêtes mutantes échoueront silencieusement.
            throw new AuthError('internal_error', 'Missing csrf_token in /auth/me response')
        }
        return { user, csrfToken }
    }

    private send(path: string, init: RequestInit): Promise<Response> {
        return fetch(this.baseURL + path, {
            ...init,
            credentials: 'include',
            // Pas de cache : les réponses auth changent à chaque login/logout.
            cache: 'no-store',
        }).catch((cause: unknown) => {
            const message = cause instanceof Error ? cause.message : 'fetch failed'
            throw new AuthError('network_error', message)
        })
    }
}

/**
 * readAuthError lit une réponse 4xx/5xx et tente de la parser au shape
 * standard `{error:{code,message}}`. Si le code remonté n'est pas dans
 * KNOWN_AUTH_CODES, on garde le status HTTP mais on classe en 'unknown'
 * (pas de propagation de codes serveur arbitraires côté UI).
 */
async function readAuthError(resp: Response): Promise<AuthError> {
    let body: unknown = null
    try {
        body = await resp.json()
    } catch {
        // Pas de JSON parsable → message générique, on garde le status.
        return new AuthError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const err = (body as { error?: { code?: unknown; message?: unknown } }).error
    if (!err || typeof err !== 'object') {
        return new AuthError('unknown', `HTTP ${resp.status}`, resp.status)
    }
    const rawCode = typeof err.code === 'string' ? err.code : 'unknown'
    const code: AuthErrorCode = (KNOWN_AUTH_CODES as readonly string[]).includes(rawCode)
        ? (rawCode as AuthErrorCode)
        : 'unknown'
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`
    return new AuthError(code, message, resp.status)
}

/**
 * parseUser valide le shape `{id, tenant_id, email, name, role}` retourné
 * par le backend (cf. controlplane/auth.go). Toute valeur manquante /
 * mal typée → AuthError. Pas de tolérance ici : un user mal formé en
 * mémoire empoisonne toute la session.
 */
function parseUser(raw: unknown): AuthenticatedUser {
    if (!raw || typeof raw !== 'object') {
        throw new AuthError('internal_error', 'Invalid user payload')
    }
    const obj = raw as Record<string, unknown>
    const id = stringField(obj.id)
    const tenantId = stringField(obj.tenant_id)
    const email = stringField(obj.email)
    const name = stringField(obj.name)
    const role = parseRole(stringFieldOrNull(obj.role))
    if (!id || !tenantId || !email || !role) {
        throw new AuthError('internal_error', 'Invalid user payload')
    }
    return { id, tenantId, email, name, role }
}

function stringField(v: unknown): string {
    return typeof v === 'string' ? v : ''
}

function stringFieldOrNull(v: unknown): string | null {
    return typeof v === 'string' ? v : null
}
